/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import apiClient from './client';
import { APIResponse, UserAuthenticationResult } from '@/types';
import { encryptForApi } from '@/utils/crypto';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ResetPasswordRequest {
  email: string;
  reseCode?: string;
  password?: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  zipCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateId?: string;
  birthDate?: string;
  isOwnerSelected?: boolean;
  isRenterSelected?: boolean;
  confirmEighteenYearOld?: boolean;
  receiveNotification?: boolean;
  adsSubscriptions?: boolean;
}

export interface SignUpResponse {
  id: string;
  email: string;
}

// Login AES key. Prefer the RUNTIME value injected by the Express host into
// window.__ENV__ (sourced from the App Service REACT_APP_AUTH_ENCRYPTION_KEY env var),
// and fall back to the build-time value only for local `npm start`. The GitHub Actions
// build ships no .env, so the build-time value is EMPTY in the deployed bundle — reading
// it at runtime is what keeps signin-secure decryption working server-side.
function getAuthEncryptionKey(): string {
  const runtime =
    typeof window !== 'undefined'
      ? (window as unknown as { __ENV__?: { AUTH_ENCRYPTION_KEY?: string } }).__ENV__
      : undefined;
  return runtime?.AUTH_ENCRYPTION_KEY || process.env.REACT_APP_AUTH_ENCRYPTION_KEY || '';
}

export const authApi = {
  login: async (data: LoginRequest): Promise<APIResponse<UserAuthenticationResult>> => {
    const payload = encryptForApi(
      JSON.stringify({ email: data.email, password: data.password }),
      getAuthEncryptionKey()
    );
    const response = await apiClient.post<APIResponse<UserAuthenticationResult>>(
      '/UserAuth/signin-secure',
      { payload }
    );
    return response.data;
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<APIResponse<string>> => {
    const response = await apiClient.post<APIResponse<string>>(
      '/UserAuth/resetpassword',
      data
    );
    return response.data;
  },

  checkEmailAvailable: async (email: string): Promise<APIResponse<boolean>> => {
    // The body is a bare JSON string, and axios sets no content type for a string payload — the
    // browser then sends text/plain and the API answers 415, since [FromBody] string is read by the
    // JSON input formatter or not at all. Say it explicitly.
    const response = await apiClient.post<APIResponse<boolean>>(
      '/Users/emails/free',
      JSON.stringify(email),
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  signUp: async (data: SignUpRequest): Promise<APIResponse<SignUpResponse>> => {
    const response = await apiClient.post<APIResponse<SignUpResponse>>(
      '/UserAuth/signup',
      data
    );
    return response.data;
  },

  verifyRegistration: async (userId: string): Promise<APIResponse<string>> => {
    const response = await apiClient.get<APIResponse<string>>(
      `/UserAuth/${userId}/registration-verify`
    );
    return response.data;
  },
};
